import { announcementsApi } from '@/api';
import { queryKeys } from '@/api/query-keys.ts';
import {
  Announcement,
  AnnouncementWriteRequest,
  Maintenance,
  MaintenanceWriteRequest,
} from '@/api/announcements/announcements.types.ts';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

export const useAnnouncements = (
  options?: UseQueryOptions<Announcement[], AxiosError>,
) => {
  return useQuery<Announcement[], AxiosError>({
    queryKey: queryKeys.announcements.all,
    queryFn: () =>
      announcementsApi.getAnnouncements().then((res) => res.data),
    ...options,
  });
};

export const useCreateAnnouncement = (
  options?: UseMutationOptions<
    Announcement,
    AxiosError,
    AnnouncementWriteRequest
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation<Announcement, AxiosError, AnnouncementWriteRequest>({
    mutationFn: (body) =>
      announcementsApi.createAnnouncement(body).then((res) => res.data),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.announcements.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.appState });
      options?.onSuccess?.(...args);
    },
  });
};

export const useUpdateAnnouncement = (
  options?: UseMutationOptions<
    Announcement,
    AxiosError,
    { id: string; body: AnnouncementWriteRequest }
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation<
    Announcement,
    AxiosError,
    { id: string; body: AnnouncementWriteRequest }
  >({
    mutationFn: ({ id, body }) =>
      announcementsApi.updateAnnouncement(id, body).then((res) => res.data),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.announcements.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.appState });
      options?.onSuccess?.(...args);
    },
  });
};

export const useDeleteAnnouncement = (
  options?: UseMutationOptions<void, AxiosError, string>,
) => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, string>({
    mutationFn: (id) =>
      announcementsApi.deleteAnnouncement(id).then((res) => res.data),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.announcements.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.appState });
      options?.onSuccess?.(...args);
    },
  });
};

export const useMaintenance = (
  options?: UseQueryOptions<Maintenance, AxiosError>,
) => {
  return useQuery<Maintenance, AxiosError>({
    queryKey: queryKeys.announcements.maintenance,
    queryFn: () => announcementsApi.getMaintenance().then((res) => res.data),
    ...options,
  });
};

export const useUpdateMaintenance = (
  options?: UseMutationOptions<
    Maintenance,
    AxiosError,
    MaintenanceWriteRequest
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation<Maintenance, AxiosError, MaintenanceWriteRequest>({
    mutationFn: (body) =>
      announcementsApi.updateMaintenance(body).then((res) => res.data),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.announcements.maintenance,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.appState });
      options?.onSuccess?.(...args);
    },
  });
};
